/**
 * Real search service — queries multiple data sources:
 * - People (GraphQL: listApplicants)
 * - Calendar (GraphQL: listCalendarEvents)
 * - Inquiries (GraphQL: listInquiries)
 * - Chat (Stream Chat channels via connected client)
 *
 * Performs client-side text filtering across all fetched entities.
 */
import { generateClient } from 'aws-amplify/api';
import { listCalendarEvents, listApplicants, listInquiries, heraldFeed } from '@/graphql/queries';
import { getCachedEvents, mapGqlEvent, type CalendarEvent } from '@/data/calendarEvents';
import { streamClient } from '@/lib/streamClient';
import type { GroupedSearchResults, SearchResultItem } from './types';

/**
 * Search across all real data sources.
 *
 * Season SSOT (ssot-cutover-cleanup R-A): this module cannot call the
 * `useActiveSeason` hook, so the operative competition year is passed IN
 * as an argument by the React caller (`useSearch`), which resolves it
 * through the active-season seam. The module never re-derives the year.
 */
export async function realSearch(
  query: string,
  operativeYear: number,
): Promise<GroupedSearchResults> {
  const normalizedQuery = query.toLowerCase().trim();
  if (!normalizedQuery) return {};

  // Fetch data in parallel (best-effort — failures return empty arrays)
  const [calendarEvents, people, inquiries, bulletin, chatChannels] = await Promise.all([
    fetchCalendarEvents(operativeYear),
    fetchPeople(),
    fetchInquiries(),
    fetchBulletin(),
    fetchChatChannels(normalizedQuery),
  ]);

  // Guidebook is no longer searchable: the contestant surface was retired
  // (2026-07-29) and a result linking to a removed route would strand the user.
  // `packages/shared`'s canonical GUIDEBOOK content is untouched.

  const grouped: GroupedSearchResults = {};

  // Calendar events
  const matchingEvents = calendarEvents.filter((event) => {
    const searchable = `${event.title} ${event.description || ''} ${event.location} ${event.type}`.toLowerCase();
    return searchable.includes(normalizedQuery);
  });
  if (matchingEvents.length > 0) {
    grouped['Calendar'] = matchingEvents.slice(0, 5).map((event): SearchResultItem => ({
      id: event.id,
      entityType: 'event',
      name: event.title,
      description: `${formatDate(event.date)}${event.time ? ` · ${event.time}` : ''} — ${event.location || 'TBD'}`,
      category: 'Calendar',
      url: '/admin/calendar',
    }));
  }

  // People
  const matchingPeople = people.filter((person) => {
    const searchable = `${person.fullName || ''} ${person.email || ''}`.toLowerCase();
    return searchable.includes(normalizedQuery);
  });
  if (matchingPeople.length > 0) {
    grouped['People'] = matchingPeople.slice(0, 5).map((person): SearchResultItem => ({
      id: person.id,
      entityType: 'person',
      name: person.fullName || person.email || 'Unknown',
      description: person.email || '',
      category: 'People',
      url: '/admin/participants',
    }));
  }

  // Inquiries
  const matchingInquiries = inquiries.filter((inq) => {
    const searchable = `${inq.name} ${inq.email} ${inq.subject} ${inq.message}`.toLowerCase();
    return searchable.includes(normalizedQuery);
  });
  if (matchingInquiries.length > 0) {
    grouped['Inquiries'] = matchingInquiries.slice(0, 5).map((inq): SearchResultItem => ({
      id: inq.id,
      entityType: 'inquiry',
      name: inq.subject || `Inquiry from ${inq.name}`,
      description: `${inq.name} — ${inq.email}`,
      category: 'Inquiries',
      url: '/admin/settings',
    }));
  }

  // Bulletin (Herald community posts)
  const matchingBulletin = bulletin.filter((post) => {
    const searchable = `${post.text || ''} ${post.authorName || ''}`.toLowerCase();
    return searchable.includes(normalizedQuery);
  });
  if (matchingBulletin.length > 0) {
    grouped['Bulletin'] = matchingBulletin.slice(0, 5).map((post): SearchResultItem => {
      const snippet = (post.text || '').replace(/\s+/g, ' ').trim();
      return {
        id: post.id,
        entityType: 'bulletin',
        name: snippet.slice(0, 60) + (snippet.length > 60 ? '…' : '') || 'Bulletin post',
        description: `${post.authorName || 'Unknown'}${post.pinned ? ' · Pinned' : ''}`,
        category: 'Bulletin',
        url: '/admin/herald',
      };
    });
  }

  // Chat channels
  if (chatChannels.length > 0) {
    grouped['Chat'] = chatChannels.slice(0, 5);
  }

  return grouped;
}

// --- Data fetchers ---

interface PersonRecord {
  id: string;
  fullName?: string;
  email?: string;
}

interface InquiryRecord {
  id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  status: string;
  createdAt: string;
}

interface BulletinRecord {
  id: string;
  text?: string;
  authorName?: string;
  pinned?: boolean;
  createdAt?: string;
}

async function fetchCalendarEvents(operativeYear: number): Promise<CalendarEvent[]> {
  try {
    const apiClient = generateClient();
    const result = (await apiClient.graphql({
      query: listCalendarEvents,
      variables: { competitionYear: operativeYear },
      authMode: 'userPool',
    })) as { data?: { listCalendarEvents?: unknown[] } };

    const events = (result.data?.listCalendarEvents ?? [])
      .map((e) => mapGqlEvent(e as Parameters<typeof mapGqlEvent>[0]));
    return events;
  } catch {
    return getCachedEvents();
  }
}

async function fetchPeople(): Promise<PersonRecord[]> {
  try {
    const apiClient = generateClient();
    const result = (await apiClient.graphql({
      query: listApplicants,
      variables: { limit: 200 },
      authMode: 'userPool',
    })) as { data?: { listApplicants?: { items?: PersonRecord[] } } };

    return result.data?.listApplicants?.items ?? [];
  } catch {
    return [];
  }
}

async function fetchInquiries(): Promise<InquiryRecord[]> {
  try {
    const apiClient = generateClient();
    const result = (await apiClient.graphql({
      query: listInquiries,
      variables: { limit: 100 },
      authMode: 'userPool',
    })) as { data?: { listInquiries?: { items?: InquiryRecord[] } } };

    return result.data?.listInquiries?.items ?? [];
  } catch {
    return [];
  }
}

async function fetchBulletin(): Promise<BulletinRecord[]> {
  try {
    const apiClient = generateClient();
    const result = (await apiClient.graphql({
      query: heraldFeed,
      variables: { limit: 50 },
      authMode: 'userPool',
    })) as { data?: { heraldFeed?: { items?: BulletinRecord[] } } };

    return result.data?.heraldFeed?.items ?? [];
  } catch {
    return [];
  }
}

async function fetchChatChannels(query: string): Promise<SearchResultItem[]> {
  try {
    // Only search if the stream client is connected
    if (!streamClient || !streamClient.user) return [];

    const channels = await streamClient.queryChannels(
      {
        type: 'messaging',
        name: { $autocomplete: query },
      },
      { last_message_at: -1 },
      { limit: 5 },
    );

    return channels.map((channel): SearchResultItem => {
      const data = channel.data as { name?: string } | undefined;
      return {
        id: channel.cid || channel.id || '',
        entityType: 'chat',
        name: data?.name || 'Unnamed channel',
        description: channel.state?.messages?.length
          ? `Last message: ${(channel.state.messages[channel.state.messages.length - 1]?.text || '').slice(0, 60)}`
          : 'No messages yet',
        category: 'Chat',
        url: `/admin/messages?channel=${channel.cid}`,
      };
    });
  } catch {
    return [];
  }
}

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('default', { month: 'short', day: 'numeric' });
  } catch {
    return dateStr;
  }
}
